import { useEffect, useRef, useState } from 'react';
import { Member } from '~/types';
import { validateEmail } from '~/utils';

const MAX_VALUE_LENGTH = 1000;

const useChatInput = (
  members: Member[],
  setShowSendButton: (show: boolean) => void,
) => {
  const [value, setValue] = useState<string>('');
  const valueRef = useRef<string>('');
  const inpRef = useRef<HTMLDivElement>(null);
  const [mentionedAIMembers, setMentionedAIMembers] = useState<Member[]>([]);
  const [mentionedUserMembers, setMentionedUserMembers] = useState<Member[]>(
    [],
  );
  const mentionedAIMembersRef = useRef<Member[]>([]);
  const mentionedUserMembersRef = useRef<Member[]>([]);
  const [bottomOffset, setBottomOffset] = useState<number>(50);

  useEffect(() => {
    valueRef.current = value;

    // Remove all instances of *direction:* with optional spaces in between and trim the result
    const strippedValue = value.replace(/\*direction:\s*\*/g, '').trim();
    const isOnlyDirectionMessage = strippedValue.length === 0;

    // Set the send button visibility based on the new condition
    setShowSendButton(!isOnlyDirectionMessage);

    const inpElem = inpRef.current;
    if (inpElem) {
      inpElem.style.height = '25px';
      inpElem.style.height = `${inpElem.scrollHeight}px`;
      setBottomOffset(inpElem.scrollHeight);
    }
  }, [value]);

  useEffect(() => {
    mentionedAIMembersRef.current = mentionedAIMembers;
  }, [mentionedAIMembers]);

  useEffect(() => {
    mentionedUserMembersRef.current = mentionedUserMembers;
  }, [mentionedUserMembers]);

  const insertHtmlAtCursor = (html: string) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const frag = document.createDocumentFragment();
    let node;
    let lastNode;
    while ((node = tempDiv.firstChild)) {
      lastNode = frag.appendChild(node);
    }
    range.deleteContents();
    range.insertNode(frag);
    if (lastNode) {
      range.setStartAfter(lastNode);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  const updateInputValue = () => {
    const text = inpRef.current?.innerText || '';

    if (text.length > MAX_VALUE_LENGTH) {
      // Prevent additional characters from being entered
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const textNode = range.startContainer;
        if (textNode.nodeType === Node.TEXT_NODE) {
          const offset = range.startOffset;
          inpRef.current!.innerText = text.slice(0, MAX_VALUE_LENGTH);
          setValue(text.slice(0, MAX_VALUE_LENGTH));

          range.setStart(textNode, Math.min(offset, MAX_VALUE_LENGTH));
          range.setEnd(textNode, Math.min(offset, MAX_VALUE_LENGTH));
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
      return;
    }

    setValue(text);
    if (inpRef.current && isCursorAtEnd()) {
      inpRef.current.scrollTop = inpRef.current.scrollHeight;
    }
    const mentions = Array.from(
      inpRef.current?.getElementsByClassName('mention') || [],
    ).map((mention) => mention.id);
    const resetMembers = mentions.map((mentionId) =>
      members.find((member) => member.id === mentionId),
    );
    const AIMembers = resetMembers.filter(
      (member): member is Member =>
        member !== undefined && !validateEmail(member.id),
    );
    const userMembers = resetMembers.filter(
      (member): member is Member =>
        member !== undefined && validateEmail(member.id),
    );

    setMentionedAIMembers(AIMembers);
    setMentionedUserMembers(userMembers);
    mentionedAIMembersRef.current = AIMembers;
    mentionedUserMembersRef.current = userMembers;
  };

  const isCursorAtEnd = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return false;
    const range = selection.getRangeAt(0);
    const cursorPosition = range.endOffset;
    const textLength = range.endContainer.textContent?.length || 0;
    return cursorPosition === textLength;
  };

  const handleDirectionMsg = () => {
    if (inpRef.current) {
      const isFocused = document.activeElement === inpRef.current;
      if (!isFocused) {
        inpRef.current.focus();
      }

      const range = document.createRange();
      const selection = window.getSelection();
      if (!selection) return;

      // Check if the input is empty
      if (inpRef.current.innerHTML === '<br>') {
        inpRef.current.innerHTML = ''; // Clear the <br> tag
      }

      range.selectNodeContents(inpRef.current);
      range.collapse(false);

      selection.removeAllRanges();
      selection.addRange(range);

      insertHtmlAtCursor('*direction:*');

      setTimeout(() => {
        const textNode = inpRef.current?.lastChild;
        if (textNode) {
          const offset = textNode.textContent?.length || 0;
          range.setStart(textNode, offset - 1);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
        }

        updateInputValue();

        if (inpRef.current) {
          inpRef.current.scrollTop = inpRef.current.scrollHeight;
        }
      }, 0);
    }
  };

  const handlePaste = (event: ClipboardEvent) => {
    event.preventDefault();
    const text = event.clipboardData?.getData('text/plain');
    if (text) {
      insertHtmlAtCursor(text);
      updateInputValue();
      if (inpRef.current) {
        inpRef.current.scrollTop = inpRef.current.scrollHeight;
      }
    }
  };

  return {
    value,
    setValue,
    valueRef,
    inpRef,

    insertHtmlAtCursor,
    updateInputValue,
    bottomOffset,
    setMentionedAIMembers,
    mentionedAIMembersRef,
    mentionedUserMembers,
    mentionedUserMembersRef,
    handleDirectionMsg,
    handlePaste,
  };
};

export default useChatInput;
